import cn from "classnames";

import { memo, useEffect, useRef, useState } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";

/**
 * button used for triggering webcam or screen-capture
 */
const MediaStreamButton = memo(
  ({ isStreaming, onIcon, offIcon, start, stop }) =>
    isStreaming ? (
      <button
        className="flex items-center justify-center bg-neutral-20 text-neutral-60 text-xl cursor-pointer transition-all duration-200 w-12 h-12 rounded-[18px] border border-transparent hover:bg-transparent hover:border-neutral-20 focus:border-2 focus:border-neutral-20 focus:outline focus:outline-2 focus:outline-neutral-80"
        onClick={stop}
      >
        <span className="material-symbols-outlined">{onIcon}</span>
      </button>
    ) : (
      <button
        className="flex items-center justify-center bg-neutral-20 text-neutral-60 text-xl cursor-pointer transition-all duration-200 w-12 h-12 rounded-[18px] border border-transparent hover:bg-transparent hover:border-neutral-20 focus:border-2 focus:border-neutral-20 focus:outline focus:outline-2 focus:outline-neutral-80"
        onClick={start}
      >
        <span className="material-symbols-outlined">{offIcon}</span>
      </button>
    ),
);

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
}) {
  const videoStream = useWebcam();
  const [activeVideoStream, setActiveVideoStream] = useState(null);
  const webcam = videoStream;
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const renderCanvasRef = useRef(null);
  const connectButtonRef = useRef(null);

  const { client, connected, connect, disconnect, volume } =
    useLiveAPIContext();

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`,
    );
  }, [inVolume]);

  useEffect(() => {
    const onData = (base64) => {
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    if (connected && !muted && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", setInVolume).start();
    } else {
      audioRecorder.stop();
    }
    return () => {
      audioRecorder.off("data", onData).off("volume", setInVolume);
    };
  }, [connected, client, muted, audioRecorder]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = activeVideoStream;
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStream.stop();
  };

  const handleCameraSwitch = async () => {
    if (webcam.switchCamera && webcam.isStreaming) {
      const mediaStream = await webcam.switchCamera();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    }
  };

  return (
    <section className="absolute bottom-0 left-1/2 -translate-x-1/2 inline-flex justify-center items-start gap-2 pb-[18px]">
      <canvas className="hidden" ref={renderCanvasRef} />
      <nav
        className={cn(
          "bg-[#111010] border border-neutral-30 rounded-[27px] inline-flex gap-3 items-center overflow-clip p-[10px] transition-all duration-600",
          {
            "pointer-events-none": !connected,
          },
        )}
      >
        <button
          className={cn(
            "flex items-center justify-center relative bg-red-500 z-10 text-black transition-all duration-200 w-12 h-12 rounded-[18px] border border-transparent hover:bg-red-400 focus:border-2 focus:border-neutral-20 focus:outline focus:outline-2 focus:outline-red-500 animate-opacity-pulse",
            {
              "bg-transparent border-neutral-30 text-neutral-30": !connected,
              "mic-button-pulse": connected && !muted,
            },
          )}
          onClick={() => setMuted(!muted)}
        >
          {!muted ? (
            <span className="material-symbols-outlined filled">mic</span>
          ) : (
            <span className="material-symbols-outlined filled">mic_off</span>
          )}
        </button>

        <div className="flex items-center justify-center bg-neutral-2 border border-neutral-20 w-12 h-12 rounded-[18px] pointer-events-none">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>

        {supportsVideo && (
          <>
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
            />
            {webcam.isStreaming && webcam.switchCamera && (
              <button
                className="flex items-center justify-center bg-neutral-20 text-neutral-60 text-xl cursor-pointer transition-all duration-200 w-12 h-12 rounded-[18px] border border-transparent hover:bg-blue-500 hover:border-blue-300 focus:border-2 focus:border-neutral-20 focus:outline focus:outline-2 focus:outline-blue-500 animate-opacity-pulse"
                onClick={handleCameraSwitch}
                title={`Switch to ${webcam.facingMode === "user" ? "back" : "front"} camera`}
              >
                <span className="material-symbols-outlined filled">
                  flip_camera_android
                </span>
              </button>
            )}
          </>
        )}

        {children}
      </nav>

      <div
        className={cn("flex flex-col justify-center items-center gap-1", {
          connected,
        })}
      >
        <div className="rounded-[27px] border border-neutral-30 bg-[#111010] p-[10px]">
          <button
            ref={connectButtonRef}
            className={cn(
              "flex items-center justify-center w-12 h-12 rounded-[18px] border border-transparent focus:border-2 focus:border-neutral-20 focus:outline focus:outline-2 focus:outline-neutral-80 transition-all duration-200 animate-opacity-pulse",
              {
                "bg-blue-500 text-neutral-5": !connected,
                "bg-neutral-20 text-neutral-60": connected,
              },
            )}
            onClick={connected ? disconnect : connect}
          >
            <span className="material-symbols-outlined filled">
              {connected ? "pause" : "play_arrow"}
            </span>
          </button>
        </div>
        <span
          className={cn("text-xs text-blue-500 select-none", {
            "opacity-0": !connected,
          })}
        >
          Streaming
        </span>
      </div>
    </section>
  );
}

export default memo(ControlTray);
